import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from 'v2/layouts/OrganizationWorkspaceLayout/components/InvitationsTableSection/constants';
import type { InvitationResponse } from 'v2/layouts/OrganizationWorkspaceLayout/components/InvitationsTableSection/types';

import useInviteUserToOrganization from 'hooks/useInviteUserToOrganization';

const useInvitationsTable = () => {
    const { deleteInvitation, getInvitations, inviteUserToOrganization } =
        useInviteUserToOrganization();

    const [invitations, setInvitations] = useState<InvitationResponse[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [pendingDeleteInvitationId, setPendingDeleteInvitationId] = useState<string | null>(null);

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const response = await getInvitations({
                pageToken: pageToken ?? undefined,
                pageable: { size: DEFAULT_PAGE_SIZE }
            });

            setInvitations(currentInvitations =>
                pageToken ? [...currentInvitations, ...response.objects] : response.objects
            );
            setHasNextPage(response.hasNext);
            setNextPageToken(response.nextPageToken ?? null);
        },
        [getInvitations]
    );

    // getInvitations broadcasts its own failure toast before rethrowing.
    const reload = useCallback(async () => {
        try {
            await loadPage();
        } catch {
            setInvitations([]);
        }
    }, [loadPage]);

    useEffect(() => {
        const loadFirstPage = async () => {
            await reload();
            setIsLoading(false);
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            setHasNextPage(false);
        } finally {
            setIsFetchingMore(false);
        }
    }, [hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleResend = useCallback(
        async (invitation: InvitationResponse) => {
            await inviteUserToOrganization({
                invitee: invitation.invitee,
                roles: invitation.roles
            });

            await reload();
        },
        [inviteUserToOrganization, reload]
    );

    const handleDeleteRequest = useCallback(
        (invitationId: string) => setPendingDeleteInvitationId(invitationId),
        []
    );

    const handleDeleteCancel = useCallback(() => setPendingDeleteInvitationId(null), []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!pendingDeleteInvitationId) {
            return;
        }

        await deleteInvitation(pendingDeleteInvitationId);

        setPendingDeleteInvitationId(null);

        await reload();
    }, [deleteInvitation, pendingDeleteInvitationId, reload]);

    return {
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleLoadMore,
        handleResend,
        hasNextPage,
        invitations,
        isDeleteConfirmOpen: pendingDeleteInvitationId !== null,
        isFetchingMore,
        isLoading
    };
};

export default useInvitationsTable;
